import dayjs from "dayjs";

export const formatPolicyDate = (value) =>
  value ? dayjs(value).format("DD MMM YY") : "—";

export const formatPolicyDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "—";
  const raw = String(value);
  const hasTime =
    raw.includes("T") || /\d{1,2}:\d{2}/.test(raw) || parsed.hour() || parsed.minute() || parsed.second();
  return hasTime ? parsed.format("DD MMM YY, hh:mm A") : parsed.format("DD MMM YY");
};

export const getTravellerName = (row) =>
  [row?.first_name, row?.last_name].filter(Boolean).join(" ") || row?.user_name || "—";

export const getTravellerInitials = (row) => {
  const name = getTravellerName(row);
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";
};

export const getTravellerAge = (dob) => {
  if (!dob) return null;
  const years = dayjs().diff(dayjs(dob), "year");
  return Number.isFinite(years) ? years : null;
};

const GENDER_LABELS = { 1: "Male", 2: "Female", 3: "Other", Male: "Male", Female: "Female", Other: "Other" };

export const formatGender = (gender) => GENDER_LABELS[gender] || (gender != null ? String(gender) : null);

export const formatAmount = (currency, amount) => {
  const val = Number(amount);
  if (!Number.isFinite(val)) return "—";
  return `${currency || "INR"} ${val.toLocaleString("en-IN")}`;
};

export const purchaseMethodLabel = (value) => {
  const map = { LINK: "LINK", BULK: "BULK", SINGLE: "BULK", online: "online" };
  return map[value] || value || "";
};

export const canShowTripPaymentActions = (trip) => {
  if (!trip?.uuid) return false;
  if (String(trip.status || "").toLowerCase() !== "pending") return false;

  const start = trip.coverage_start_date
    ? dayjs(trip.coverage_start_date).startOf("day")
    : null;
  if (!start?.isValid()) return false;

  const today = dayjs().startOf("day");
  return today.isBefore(start) || today.isSame(start);
};

const journeyDuration = (row) => {
  const start = row?.coverage_start_date ? dayjs(row.coverage_start_date).startOf("day") : null;
  const end = row?.coverage_end_date ? dayjs(row.coverage_end_date).startOf("day") : null;
  if (start?.isValid() && end?.isValid()) return `${end.diff(start, "day") + 1}d`;
  if (row?.duration != null) return `${Number(row.duration) + 1}d`;
  return "—";
};

export const getJourneyInfo = (row) => {
  const apiStatus = String(row?.trip_status || "").toLowerCase();
  if (apiStatus) {
    const phase = apiStatus.toUpperCase();
    const duration = journeyDuration(row);
    let hint = null;
    if (phase === "ACTIVE" && row.remaining_days != null) hint = `${row.remaining_days}d left`;
    else if (phase === "INACTIVE" && row.days_to_start > 0) hint = `Starts in ${row.days_to_start}d`;
    else if (phase === "EXPIRED") hint = "Ended";
    return { phase, duration, hint };
  }

  const start = row?.coverage_start_date ? dayjs(row.coverage_start_date).startOf("day") : null;
  const end = row?.coverage_end_date ? dayjs(row.coverage_end_date).startOf("day") : null;
  const today = dayjs().startOf("day");

  if (!start?.isValid() || !end?.isValid()) {
    return { phase: "INACTIVE", duration: journeyDuration(row), hint: null };
  }

  const duration = journeyDuration(row);
  if (today.isBefore(start)) {
    return { phase: "INACTIVE", duration, hint: `Starts in ${start.diff(today, "day")}d` };
  }
  if (today.isAfter(end)) return { phase: "EXPIRED", duration, hint: "Ended" };
  return { phase: "ACTIVE", duration, hint: `${end.diff(today, "day")}d left` };
};

const SIMPLIFIED_STATUS_VALUES = new Set(["active", "inactive", "expired"]);

const normalizeSimplifiedStatus = (value) => {
  const key = String(value).trim();
  if (SIMPLIFIED_STATUS_VALUES.has(key)) return key;
  const map = { Active: "active", Inactive: "inactive", Expired: "expired" };
  return map[key] || key.toLowerCase();
};

export const buildInlineFilterQuery = ({
  status,
  tripStatus,
  coverType,
  dateRange,
}) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (tripStatus) {
    const simplified = normalizeSimplifiedStatus(tripStatus);
    if (SIMPLIFIED_STATUS_VALUES.has(simplified)) {
      params.set("simplified_status", simplified);
    }
  }
  if (coverType) params.set("cover_type", coverType);
  const startKey = "start_date";
  const endKey = "end_date";
  if (dateRange?.[0]) {
    params.set(startKey, dayjs(dateRange[0]).format("YYYY-MM-DD"));
  }
  if (dateRange?.[1]) {
    params.set(endKey, dayjs(dateRange[1]).format("YYYY-MM-DD"));
  }
  return params.toString();
};
